import React, {
    createContext,
    useContext,
    useEffect,
    useRef,
    useState,
} from 'react';
import * as signalR from '@microsoft/signalr';
import { BASE_URL } from '../utils/config';
import { useStateSelector } from '../store/selectors';
import { useDispatch } from 'react-redux';
import { LoadCurrentUserIfNotSet } from '../slices/current-user/load-current-user-if-not-set.thunk';

interface ISignalRContext {
    connection: signalR.HubConnection | null;
    isConnected: boolean;
}

const SignalRContext = createContext<ISignalRContext>({
    connection: null,
    isConnected: false,
});

export const useSignalR = () => useContext(SignalRContext);

export const SignalRProvider: React.FC<{ children: React.ReactNode }> = ({
    children,
}) => {
    const dispatch = useDispatch() as any;
    const user = useStateSelector((s) => s.currentUser.user);
    const [connection, setConnection] = useState<signalR.HubConnection | null>(
        null
    );
    const [isConnected, setIsConnected] = useState(false);
    // Bumped to build a fresh connection after a failed start() or a terminal
    // close. Automatic reconnect covers neither case, so without this a single
    // failed handshake left the whole session without live updates (and blank
    // clubhouse chats) until a full page reload.
    const [connectAttempt, setConnectAttempt] = useState(0);
    const retryTimeoutRef = useRef<number>();

    useEffect(() => {
        const token = localStorage.getItem('token');
        if (token) {
            dispatch(LoadCurrentUserIfNotSet(true));
        }
    }, []);

    useEffect(() => {
        const token = localStorage.getItem('token');
        if (!user || user?.id === 0) {
            connection?.stop();
            setConnection(null);
            setIsConnected(false);
            return;
        }
        if (!token || (connection && isConnected)) return;

        // Mirrors the auto-reconnect pacing: quick retries first, then back off.
        const retryDelay = connectAttempt < 5 ? 5000 : 30000;
        const scheduleRetry = () => {
            window.clearTimeout(retryTimeoutRef.current);
            retryTimeoutRef.current = window.setTimeout(
                () => setConnectAttempt((attempt) => attempt + 1),
                retryDelay
            );
        };

        let url = BASE_URL == '/' ? window.location.origin : BASE_URL;
        const hubConnection = new signalR.HubConnectionBuilder()
            .withUrl(`${url}/hubs/vetvalue`, {
                accessTokenFactory: () => localStorage.getItem('token') ?? '',
                skipNegotiation: true,
                transport: signalR.HttpTransportType.WebSockets,
            })
            .withAutomaticReconnect({
                nextRetryDelayInMilliseconds: (retryContext) =>
                    retryContext.elapsedMilliseconds < 120000 ? 5000 : 30000,
            })
            .configureLogging(signalR.LogLevel.Warning)
            .build();

        hubConnection.onclose(() => {
            console.log('🔌 Connection closed');
            setIsConnected(false);
            // Terminal close (server closed us / reconnect gave up): automatic
            // reconnect no longer applies to this connection — build a new one.
            scheduleRetry();
        });

        hubConnection.onreconnecting(() => {
            console.log('🔄 Reconnecting...');
            setIsConnected(false);
        });

        hubConnection.onreconnected(() => {
            console.log('✅ Reconnected');
            setIsConnected(true);
            setConnectAttempt(0);
        });

        let disposed = false;
        hubConnection
            .start()
            .then(() => {
                if (disposed) {
                    hubConnection.stop();
                    return;
                }
                console.log('✅ SignalR connected');
                setConnection(hubConnection);
                setIsConnected(true);
                // Healthy again — restore the quick-retry pacing for the next
                // outage instead of staying on the slow backoff tier forever.
                setConnectAttempt(0);
            })
            .catch((err) => {
                console.error('❌ Connection failed: ', err);
                if (disposed) return;
                setIsConnected(false);
                // A failed initial start() is not covered by automatic
                // reconnect — retry it, or the session never gets a socket.
                scheduleRetry();
            });

        return () => {
            disposed = true;
            window.clearTimeout(retryTimeoutRef.current);
        };
    }, [user, connectAttempt]);

    return (
        <SignalRContext.Provider value={{ connection, isConnected }}>
            {children}
        </SignalRContext.Provider>
    );
};
